import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
} from '@mui/material'
import { useState } from 'react'
import {
  usePrintSaleAmendmentEnMutation,
  usePrintSaleAmendmentVnMutation,
  type SaleAmendment,
} from '../../store/api/saleAmendmentsApi'

interface SaleAmendmentPrintDialogProps {
  open: boolean
  language: 'en' | 'vn'
  amendment: SaleAmendment | null
  onClose: () => void
}

function buildPreviewHtml(rawHtml: string): string {
  const htmlWithoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = htmlWithoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="sale-amendment-preview-overrides">
@page{size:A4;margin:0;}
.print-action{display:none!important;}
@media print{.report-page{width:210mm!important;max-width:none!important;min-height:297mm!important;padding:12mm 14mm!important;}}
</style>`
  if (withBase.includes('</head>')) return withBase.replace('</head>', `${styleTag}</head>`)
  return `${styleTag}${withBase}`
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

export default function SaleAmendmentPrintDialog({ open, language, amendment, onClose }: SaleAmendmentPrintDialogProps) {
  const [previewing, setPreviewing] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [printEn] = usePrintSaleAmendmentEnMutation()
  const [printVn] = usePrintSaleAmendmentVnMutation()

  const handlePreview = async () => {
    if (!amendment) return
    setPreviewing(true)
    try {
      const result =
        language === 'vn'
          ? await printVn(amendment.amendmentNo).unwrap()
          : await printEn(amendment.amendmentNo).unwrap()
      setPreviewHtml(buildPreviewHtml(result.html))
      setToastMessage(result.message ?? 'Print preview generated.')
      onClose()
    } catch {
      // keep dialog open on error
    } finally {
      setPreviewing(false)
    }
  }

  const handleCancel = () => {
    setPreviewing(false)
    onClose()
  }

  const title = language === 'vn' ? 'Print Sale Amendment - VN' : 'Print Sale Amendment - EN'
  const previewTitle = language === 'vn' ? 'Print Preview - Sale Amendment VN' : 'Print Preview - Sale Amendment EN'
  const iframeTitle = language === 'vn' ? 'Sale Amendment VN Preview' : 'Sale Amendment EN Preview'

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Amendment No" size="small" value={amendment?.amendmentNo ?? ''} InputProps={{ readOnly: true }} />
            <TextField label="Contract No" size="small" value={amendment?.contractNo ?? ''} InputProps={{ readOnly: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} size="small">
            Cancel
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button variant="contained" size="small" onClick={handlePreview} disabled={!amendment || previewing}>
              Preview &amp; Print
            </Button>
            {previewing && (
              <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-10px', ml: '-10px' }} />
            )}
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(previewHtml)} onClose={() => setPreviewHtml('')} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box component="span">{previewTitle}</Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setPreviewHtml('')} size="small">
              Close
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const frame = document.querySelector<HTMLIFrameElement>(`iframe[title="${iframeTitle}"]`)
                const originalTitle = document.title
                const printTitle = `${sanitizePdfFileNamePart(amendment?.amendmentNo ?? 'amendment')}_${language}.pdf`
                document.title = printTitle
                if (frame?.contentDocument) frame.contentDocument.title = printTitle
                frame?.contentWindow?.focus()
                frame?.contentWindow?.print()
                window.setTimeout(() => {
                  document.title = originalTitle
                }, 1000)
              }}
            >
              Print
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2000}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  )
}
